import React from 'react';
import styled from 'styled-components';
import validator from 'validator';
import { activity } from 'dooh-utils';
import { Tap } from '../Tap';
import Keyboard, {
  HR_LAYOUT,
  EMAIL_LAYOUT,
  BIRTH_LAYOUT,
  NAME_LAYOUT,
  NUM_LAYOUT,
} from './Keyboard';
import Terms from './Terms';

const Wrapper = styled.div`
  width: ${props => props.width || '1080px'};
`;

const ReviewWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  & > div:first-child {
    width: 700px;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  padding: 0 25px 30px 25px;
  align-items: center;
`;

const Input = styled.div`
  width: 700px;
  margin: 10px 0;
  background: white;
  padding: 30px 25px;
  text-transform: uppercase;
  font-weight: bold;
  text-align: left;
  box-sizing: border-box;
  border: none;
  border-radius: 5px;
  font-family: ${({ theme }) => theme.inputFont || 'Roboto Bold'};
  font-size: 30px;
  color: #444;
  font-style: normal;
  border: 1px solid #aaa;
  overflow: hidden;

  &.focused {
    box-shadow: 0px 0px 5px grey;
    border: 1px solid grey;
    color: grey;
  }
`;

const Marker = styled.div`
  display: inline-block;
  position: relative;
  top: 9px;
  left: 8px;
  width: 5px;
  height: 40px;
  background: #777;

  @keyframes blink {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.1;
    }
    100% {
      opacity: 1;
    }
  }

  animation-duration: 1.3s;
  animation-iteration-count: infinite;
  animation-name: blink;
`;

const Suffix = styled.div`
  opacity: 0.5;
  display: inline-block;
  margin-left: 15px;
`;

const Button = styled.div`
  transition: opacity 0.4s;
  text-transform: ${({ theme }) =>
    theme.buttonUppercase ? 'uppercase' : 'none'};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  margin: 0 0 0 20px;

  & > div {
    width: 190px;
    text-align: center;
    background: ${({ theme }) => theme.buttonBackground || '#FE0000'};
    background-size: contain;
    color: ${({ theme }) => theme.buttonTextColor || 'white'};
    padding: 35px 25px 25px 25px;
    font-family: ${({ theme }) => theme.font || 'Roboto'};
    font-size: 45px;
    cursor: pointer;
    line-height: 48px;
    border-radius: 10px;
  }
`;

const TermsLine = styled.div`
  display: flex;
  color: ${({ theme }) => theme.termsLineColor || 'white'};
  font-family: ${({ theme }) => theme.font || 'Roboto'};
  font-size: 25px;
  padding: 0;
  text-transform: uppercase;

  & > * {
    padding: 25px 0;
  }

  & > *:first-child {
    padding: 25px 0 25px 50px;
    margin-left: -50px;
  }

  .terms {
    text-decoration: underline;
    padding-left: 10px;
  }
`;

const Disclaimer = styled.div`
  font-size: 25px;
  color: white;
  max-width: 700px;
  padding-top: 50px;
  font-family: ${({ theme }) => theme.font || 'Roboto'};
`;

const Checkbox = styled.div`
  width: 1em;
  height: 1em;
  box-sizing: border-box;
  position: relative;
  display: inline-block;
  border: 0.15em solid ${({ theme }) => theme.checkboxOuterColor || 'white'};
  border-radius: 0.8em;
  margin: 0 10px 0 0;
  bottom: 2px;
  background: ${({ selected, theme }) =>
    selected ^ !!theme.checkboxInvert
      ? theme.checkboxInnerColor || 'red'
      : theme.checkboxOuterColor || 'white'};
`;

const InputTermsWrap = styled.div`
  width: ${props => props.width || '1080px'};
  margin: -50px 70px 10px 70px;
`;

// TODO style the form better (margin-left is a hack below)
const Over18 = styled.div`
  font-size: 25px;
  color: white;
  font-family: ${({ theme }) => theme.font || 'Roboto'};
  padding: 10px 0;
  margin: -45px 0 20px 90px;
`;

export default class Form extends React.Component {
  static defaultProps = {
    over18: false,
    altTerms: [],
  };

  constructor(props) {
    super(props);
    const values = {};
    props.fields.forEach(({ name }) => {
      values[name] = '';
    });
    this.state = {
      values,
      focus: props.fields[0].name,
      showTerms: false,
      acceptTerms: false,
      altTerms: props.altTerms.map(t =>
        t.checked == null ? false : t.checked
      ),
      review: false,
    };
  }

  render() {
    if (this.props.fields.length === 1) {
      return (
        <Wrapper>
          <InputWrapper>
            <Input>
              {this.getShowValue()}
              <Marker />
              <Suffix>{this.getSuffix()}</Suffix>
            </Input>
          </InputWrapper>
          {this.getOver18Message()}
          <InputTermsWrap>{this.getTerms()}</InputTermsWrap>
          <Keyboard
            layout={this.getKeyboardLayout()}
            onKey={this.handleKey}
            isKeyEnabled={this.isKeyEnabled}
          />

          {this.state.showTerms && (
            <Terms terms={this.props.terms} onClose={this.hideTerms} />
          )}
        </Wrapper>
      );
    } else if (this.state.review) {
      return (
        <ReviewWrapper>
          <div>
            {this.props.fields.map(this.getReviewField)}
            {this.getTerms()}
            <Button className="Button" disabled={!this.isValid()}>
              <Tap onTap={this.handleSubmit} preventDoubleTap={false}>
                Dalje
              </Tap>
            </Button>
          </div>

          {this.state.showTerms && (
            <Terms terms={this.props.terms} onClose={this.hideTerms} />
          )}

          {this.props.disclaimer && (
            <Disclaimer className="disclaimerMsg">
              {this.props.disclaimer}
            </Disclaimer>
          )}
        </ReviewWrapper>
      );
    } else {
      return (
        <Wrapper>
          <InputWrapper>
            <Input>
              {this.getShowValue()}
              <Marker />
              <Suffix>{this.getSuffix()}</Suffix>
            </Input>
            <Button
              className="Button"
              disabled={!this.isFieldValid(this.state.focus)}
            >
              <Tap
                onTap={() => this.handleKey('enter')}
                preventDoubleTap={false}
              >
                Dalje
              </Tap>
            </Button>
          </InputWrapper>
          {this.getOver18Message()}
          <Keyboard
            layout={this.getKeyboardLayout()}
            onKey={this.handleKey}
            isKeyEnabled={this.isKeyEnabled}
          />
        </Wrapper>
      );
    }
  }

  getTerms() {
    return (
      <>
        {this.props.terms && (
          <TermsLine className="termsLine">
            <Tap onTap={this.toggleAcceptTerms} preventDoubleTap={false}>
              <Checkbox selected={this.state.acceptTerms} />
            </Tap>
            <Tap onTap={this.toggleAcceptTerms} preventDoubleTap={false}>
              Slažem se s
            </Tap>
            <Tap
              className="terms"
              onTap={this.showTerms}
              preventDoubleTap={false}
            >
              pravilima natječaja
            </Tap>
          </TermsLine>
        )}

        {this.props.customTerms && (
          <TermsLine className="termsLine">
            <Tap onTap={this.toggleAcceptTerms} preventDoubleTap={false}>
              <Checkbox selected={this.state.acceptTerms} />
            </Tap>
            <Tap onTap={this.toggleAcceptTerms} preventDoubleTap={false}>
              {this.props.customTerms}
            </Tap>
          </TermsLine>
        )}

        {this.props.altTerms.map(({ text }, i) => {
          return (
            <TermsLine key={i} className="termsLine">
              <Tap
                onTap={() => this.toggleAltTerms(i)}
                preventDoubleTap={false}
              >
                <Checkbox selected={this.state.altTerms[i]} />
              </Tap>
              <Tap
                onTap={() => this.toggleAltTerms(i)}
                preventDoubleTap={false}
              >
                {text}
              </Tap>
            </TermsLine>
          );
        })}
      </>
    );
  }

  getKeyboardLayout() {
    const { type } = this.getFieldOptions(this.state.focus);
    switch (type) {
      case 'name':
        return NAME_LAYOUT;
      case 'phone':
      case 'age':
        return NUM_LAYOUT;
      case 'birth':
        return BIRTH_LAYOUT;
      case 'email':
        let splitName = (this.state.values.name || ' ').split(' '), // TODO find values with type name
          firstName = splitName[0],
          lastName = splitName[1];
        let specKeys = '.@-_+'.split('').map(k => ({ label: k, value: k }));

        const isValid = value =>
          value &&
          value.length > 1 &&
          value.length < 10 &&
          value !== 'enter' &&
          value !== 'space' &&
          value !== 'backspace';

        if (isValid(firstName)) {
          specKeys.push({
            label: firstName,
            value: firstName,
            className: 'small',
          });
        }

        if (isValid(lastName)) {
          specKeys.push({
            label: '.',
            value: '.',
          });
          specKeys.push({
            label: lastName,
            value: lastName,
            className: 'small',
          });
        }

        specKeys.push(
          { value: '@gmail.com', label: '@gmail.com', className: 'small' },
          { value: '.com', label: '.com', className: 'small' },
          { value: '.hr', label: '.hr', className: 'small' }
        );

        return [
          {
            groupName: 'emailSpecial',
            keys: specKeys,
          },
          ...EMAIL_LAYOUT,
        ];
      default:
        return HR_LAYOUT;
    }
  }

  isOver18 = fieldValue => {
    if (fieldValue.length < 8) return false;
    const date = new Date(),
      todayDay = date.getDate(),
      todayMonth = date.getMonth() + 1,
      todayYear = date.getFullYear(),
      birthDay = parseInt(fieldValue.substr(0, 2), 10),
      birthMonth = parseInt(fieldValue.substr(2, 2), 10),
      birthYear = parseInt(fieldValue.substr(4, 4), 10);
    if (todayYear - birthYear > 18) return true;
    if (todayYear - birthYear < 18) return false;
    if (todayMonth > birthMonth) return true;
    if (todayMonth < birthMonth) return false;
    if (todayDay >= birthDay) return true;
    return false;
  };

  getOver18Message = keyValue => {
    if (!this.props.over18) return null;

    const s = this.state,
      { type } = this.getFieldOptions(s.focus),
      val = this.state.values[s.focus] || '';

    if (type !== 'birth' || val.length !== 8) return null;

    return this.isOver18(val) ? null : (
      <Over18 className="over18msg">
        Za sudjelovanje morate biti punoljetni
      </Over18>
    );
  };

  isKeyEnabled = keyValue => {
    const s = this.state,
      { type } = this.getFieldOptions(s.focus),
      val = this.state.values[s.focus] || '',
      len = val.length,
      last = len > 0 ? val[len - 1] : null;

    if (type === 'age' && keyValue === 'enter') {
      return this.isFieldValid(s.focus);
    } else if (type === 'birth') {
      if (keyValue === '.') {
        return len === 1 || len === 3;
      } else if (keyValue.match(/^[0-9]$/) != null) {
        switch (len) {
          case 0:
            return true;
          case 1:
            if (last === '0' && keyValue === '0') return false;
            if (last === '0' || last === '1' || last === '2') return true;
            return keyValue === '0' || keyValue === '1';
          case 2:
            return true;
          case 3:
            if (last === '0' && keyValue === '0') return false;
            if (last === '0') return true;
            return keyValue === '0' || keyValue === '1' || keyValue === '2';
          case 4:
            return keyValue === '1' || keyValue === '2';
          case 5:
            if (last === '1') return keyValue === '9';
            else return keyValue === '0';
          case 6:
            return true;
          case 7:
            return true;
          default:
            return false;
        }
      } else if (keyValue === 'backspace' && len > 0) {
        return true;
      } else if (keyValue === 'enter' && this.isFieldValid(s.focus)) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  };

  getReviewField = field => {
    return (
      <Tap
        key={field.name}
        onTap={() => this.setState({ focus: field.name, review: false })}
        preventDoubleTap
      >
        <Input>{this.state.values[field.name] || '\u00a0'}</Input>
      </Tap>
    );
  };

  isFieldValid = fieldName => {
    const value = (this.state.values[fieldName] || '').trim();
    const { type } = this.getFieldOptions(fieldName);
    if (type === 'email') {
      return validator.isEmail(value);
    } else if (type === 'birth') {
      return value.length === 8 && (!this.props.over18 || this.isOver18(value));
    } else if (type === 'age') {
      let age = parseInt(value, 10);
      return Number.isFinite(age) && age > 0 && age < 120;
    } else {
      return value.length > 0;
    }
  };

  isValid = () => {
    return (
      //this.state.acceptTerms &&
      this.props.fields.map(f => f.name).every(this.isFieldValid) &&
      this.state.altTerms.every(
        (t, i) => t || !this.props.altTerms[i].isRequired
      )
    );
  };

  getShowValue() {
    const s = this.state,
      { type, label } = this.getFieldOptions(s.focus);

    if (type === 'birth') {
      const value = s.values[s.focus] || '',
        show = [
          s => `${label}`,
          s => `${s[0]}`,
          s => `${s[0]}${s[1]}.`,
          s => `${s[0]}${s[1]}.${s[2]}`,
          s => `${s[0]}${s[1]}.${s[2]}${s[3]}.`,
          s => `${s[0]}${s[1]}.${s[2]}${s[3]}.${s[4]}`,
          s => `${s[0]}${s[1]}.${s[2]}${s[3]}.${s[4]}${s[5]}`,
          s => `${s[0]}${s[1]}.${s[2]}${s[3]}.${s[4]}${s[5]}${s[6]}`,
          s => `${s[0]}${s[1]}.${s[2]}${s[3]}.${s[4]}${s[5]}${s[6]}${s[7]}.`,
        ];
      return show[value.length](value);
    } else {
      return s.values[s.focus];
    }
  }

  getSuffix() {
    const { type } = this.getFieldOptions(this.state.focus),
      value = this.state.values[this.state.focus] || '',
      birthSuffixes = [
        '',
        'd.mm.gggg.',
        'mm.gggg.',
        'm.gggg.',
        'gggg.',
        'ggg.',
        'gg.',
        'g.',
        '',
      ];

    if (type === 'birth') {
      return birthSuffixes[value.length];
    } else {
      return '';
    }
  }

  getFieldOptions(field) {
    return this.props.fields.find(f => f.name === field);
  }

  handleBirthKey = key => {
    const s = this.state;
    let val = s.values[s.focus],
      len = val.length;

    if (len === 8 && key !== 'backspace' && key !== 'enter') return;

    if (key === '.') {
      if (len === 1) val = `0${val}`;
      else if (len === 3) val = `${val[0]}${val[1]}0${val[2]}`;
    } else if (key === 'backspace') {
      val = val.substr(0, val.length - 1);
    } else if (key === 'enter') {
      // TODO duplicated
      if (!this.isFieldValid(s.focus)) return;

      let fields = this.props.fields.map(f => f.name),
        index = (fields.indexOf(s.focus) + 1) % fields.length,
        field = fields[index];
      this.setState({ focus: field, review: index === 0 });
      return;
    } else if (key.match(/^[0-9]$/) != null) {
      if (len === 0 && key.match(/[4-9]/) != null) {
        val += '0' + key;
      } else if (len === 2 && key.match(/[2-9]/) != null) {
        val += '0' + key;
      } else {
        val += key;
      }
    }

    this.setState({ values: { ...s.values, [s.focus]: val } });
  };

  handleKey = key => {
    activity();

    const s = this.state,
      { type } = this.getFieldOptions(s.focus);

    if (type === 'birth') {
      this.handleBirthKey(key);
      return;
    }

    let val = s.values[s.focus];

    if (key === 'space') {
      val = val + ' ';
    } else if (key === 'backspace') {
      val = val.substr(0, val.length - 1);
    } else if (key === 'enter') {
      if (!this.isFieldValid(s.focus)) return;
      if (this.props.fields.length === 1) {
        this.handleSubmit();
        return;
      }

      let fields = this.props.fields.map(f => f.name),
        index = (fields.indexOf(s.focus) + 1) % fields.length,
        field = fields[index];
      this.setState({ focus: field, review: index === 0 });
      return;
    } else if (val.length < 300) {
      val = val + key;
    }

    this.setState({ values: { ...s.values, [s.focus]: val } });
  };

  handleSubmit = () => {
    if (this.isValid()) this.props.onSubmit(this.state.values);
  };

  toggleAcceptTerms = () => {
    this.setState(s => ({ ...s, acceptTerms: !s.acceptTerms }));
  };

  toggleAltTerms = i => {
    const altTerms = [...this.state.altTerms];
    altTerms[i] = !altTerms[i];
    this.setState({ altTerms });
  };

  showTerms = () => this.setState({ showTerms: true });
  hideTerms = () => this.setState({ showTerms: false });
}
